import {
    accessDelegationListQueryAtom,
    type AccessDelegationWithType,
} from "@/modules/access/delegation/accessDelegationApi.ts";
import { apiTokenListQueryAtom, type ApiTokenWithType } from "@/modules/access/token/apiTokenApi.ts";
import { resourceTypesEnabledAtom, type ResourceType } from "@/modules/api/resourceTypes.tsx";
import { allAssignedVmsQueryAtom, type VirtualMachineWithAssigned } from "@/modules/compute/joinAssignedQuery.ts";
import type { RecentResource } from "@/modules/dashboard/recentQuery.ts";
import type { MetalCommitmentWithEndsAt } from "@/modules/metal/commitment/commitment.ts";
import { allMetalPricedQueryAtom, type MetalListItemWithPrice } from "@/modules/metal/joinPricesQuery.ts";
import {
    allAssignedIpsQueryAtom,
    allAssignedLbsQueryAtom,
    type IpAddressWithPrice,
    type LoadBalancerWithPrice,
} from "@/modules/network/ipAddress/joinAssignedQuery.ts";
import type { Price } from "@/modules/pricing/resourcePricing.ts";
import { allAssignedServicesQueryAtom, type ManagedServiceWithAssigned } from "@/modules/services/joinAssignedQuery.ts";
import type { StorageAccountWithPrice } from "@/modules/storage/account/storageAccountApi.ts";
import { storageAccountListQueryWithPricesAtom } from "@/modules/storage/account/storageAccountQuery.ts";
import type { BucketWithPrice } from "@/modules/storage/bucket/bucketApi";
import { bucketListQueryWithPricesAtom } from "@/modules/storage/bucket/bucketQuery.ts";
import { mergeQueries, queryThen, type QResult } from "@/utils/query/mergeQueries.ts";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { atomFamily } from "jotai-family";
import { atom, type Atom } from "jotai/vanilla";
import { mapValues, omitBy, pipe } from "remeda";

/**
 * Resource types that can be owned by billing accounts
 *
 * @see - {@link RecentResource} for a very similar concept and implementation
 */
export type BillingLinkedResource =
    | VirtualMachineWithAssigned
    | ManagedServiceWithAssigned
    | BucketWithPrice
    | StorageAccountWithPrice
    | LoadBalancerWithPrice
    | IpAddressWithPrice
    | ApiTokenWithType
    | AccessDelegationWithType
    | MetalListItemWithPrice;

type ItemsResult = Map<unknown, BillingLinkedResource> | BillingLinkedResource[];
type QueryAtom = Atom<QResult<ItemsResult>>;

/** Map from resource type to its query atom */
const billingResourceTypeToQuery = {
    virtual_machine: allAssignedVmsQueryAtom,
    managed_service: allAssignedServicesQueryAtom,
    bucket: bucketListQueryWithPricesAtom,

    vpc: undefined,
    firewall: undefined,
    disk: undefined,
    storage_account: storageAccountListQueryWithPricesAtom, // TODO: Add this (WAR-5880)
    ip_address: allAssignedIpsQueryAtom,
    load_balancer: allAssignedLbsQueryAtom,

    api_token: apiTokenListQueryAtom,
    access_delegation: accessDelegationListQueryAtom,
    access_impersonation: undefined,

    bare_metal: allMetalPricedQueryAtom,

    billing_account: undefined,
} satisfies Record<ResourceType, QueryAtom | undefined>;

/** Get owning billing account ID for a linked resource */
function getBaId(item: BillingLinkedResource) {
    return item.$type === "virtual_machine" ? item.billing_account : item.billing_account_id;
}

export interface PaygPricing {
    pricingType: "payg";
    price: Price;
    commitment?: never;
}

export interface CommitmentPricing {
    pricingType: "commitment";
    price: Price;
    commitment: MetalCommitmentWithEndsAt;
}

export type LinkedResourcePricing =
    | PaygPricing
    | CommitmentPricing
    | { pricingType: "included" }
    | { pricingType: "unpriced" };

export function getLinkedResourcePricing(item: BillingLinkedResource): LinkedResourcePricing {
    if (item.$type === "ip_address" && item.assigned_to_resource_type === "virtual_machine") {
        return { pricingType: "included" };
    }

    const { $type } = item;
    switch ($type) {
        case "virtual_machine":
        case "bucket":
        case "ip_address":
        case "load_balancer":
        case "managed_service":
            return { pricingType: "payg", price: item.price };

        case "bare_metal":
            return item.pricing;

        case "storage_account":
        case "api_token":
        case "access_delegation":
            return { pricingType: "unpriced" };

        default:
            exhaustiveSwitchCheck($type);
    }
}

const allLinkedResourcesQueryAtom = atom((get) => {
    const typeEnabled = get(resourceTypesEnabledAtom);
    const enabledQueries = pipe(
        billingResourceTypeToQuery,
        // Filter non-existent queries and disabled types
        omitBy((atom, type) => atom == null || !typeEnabled[type]),
        mapValues((atom: QueryAtom) => get(atom)),
    );

    return mergeQueries(enabledQueries, (maps) =>
        maps.flatMap((m: ItemsResult): BillingLinkedResource[] =>
            // Some of the atoms above are maps, some are arrays, so convert everything to arrays
            m instanceof Map ? [...m.values()] : m,
        ),
    );
});

export const baLinkedResourcesAtom = atomFamily((billingAccountId: number) =>
    atom((get) =>
        queryThen(get(allLinkedResourcesQueryAtom), (items) =>
            items.filter((item) => getBaId(item) === billingAccountId),
        ),
    ),
);
